import { CommonModule } from '@angular/common';
import { Component, DestroyRef, HostListener, inject, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize,forkJoin, of, timeout } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { BulkUploadValidationService, BulkUploadValidationError } from '../../core/services/bulk-upload-validation.service';
import { ReviewerService } from '../../core/services/reviewer.service';
import { RebarAuthService } from '../../core/rebarauth/rebar.auth.service';
import { UserRoleService } from '../../core/services/user-role.service';
import { DateFormatService } from '../../core/services/date-format.service';
import { ExcelExportService } from '../../core/services/excel-export.service';
import { CreateNewIssueComponent } from './create-new-issue/create-new-issue.component';
import { ColumnFilter, ColumnItem, ColumnSection } from './issue-management.interfaces';

type UploadIssueToastAction = 'upload' | 'download';
type UploadIssueToastVariant = 'success' | 'error';

interface UploadIssueToastData {
  title: UploadIssueToastVariant;
  header: string;
  content: string;
}

@Component({
  selector: 'app-issue-management',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatDialogModule, CreateNewIssueComponent],
  templateUrl: './issue-management.component.html',
  styleUrl: './issue-management.component.css',
})
export class IssueManagementComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private dateFormatSvc = inject(DateFormatService);
  private readonly dialog = inject(MatDialog);
  private readonly excelExportSvc = inject(ExcelExportService);
  private readonly uploadIssueToast = viewChild.required<TemplateRef<UploadIssueToastData>>('uploadIssueToast');
  private uploadIssueToastRef: MatDialogRef<unknown> | null = null;
  private activeUploadIssueToastAction: UploadIssueToastAction | null = null;

  /** The current review's ReviewID; needed to load/create issues. */
  @Input() reviewId: number | null = null;
  /** Passed through to the Create New Issue modal. */
  @Input() financialYear: string | null = null;
  @Input() quarter: string | null = null;
  @Input() isCancelled: boolean = false;
  @Input() reviewTypeId: number = 1;
  @Input() typeOfReview: string = '';
  @Input() isReadOnly: boolean = false;
  @Input() isViewMode: boolean = false;

  isLoading = false;
  isExporting = false;
  draftSavedMessage = false;
  successToastMessage: string | null = null;
  addIssueModalOpen = false;
  editingIssueData: any = null;

  // ── Upload Issue Modal ──
  uploadIssueModalOpen = false;
  isDownloadingIssueTemplate = false;
  isUploadingIssueFile = false;
  bulkUploadErrors: BulkUploadValidationError[] = [];

  get singleLineUploadErrors(): BulkUploadValidationError[] {
    return this.bulkUploadErrors.filter(e => e.row === undefined && e.field === undefined);
  }

  get rowLevelUploadErrors(): BulkUploadValidationError[] {
    return this.bulkUploadErrors.filter(e => e.row !== undefined || e.field !== undefined);
  }
  private readonly uploadIssueToastMessages: Record<
    UploadIssueToastAction,
    Record<UploadIssueToastVariant, Pick<UploadIssueToastData, 'header' | 'content'>>
  > = {
    upload: {
      success: {
        header: 'Upload Successful!',
        content: 'File passed validations.',
      },
      error: {
        header: 'Upload Failed!',
        content: 'Please resolve error(s) and retry.',
      },
    },
    download: {
      success: {
        header: 'Download Successful!',
        content: 'Template downloaded successfully.',
      },
      error: {
        header: 'Download Failed!',
        content: 'Please try again.',
      },
    },
  };

  ngOnInit(): void {
    const issueUpdatedHandler = (e: StorageEvent) => {
      if (e.key === 'ast_issue_updated') this.loadIssues();
    };
    window.addEventListener('storage', issueUpdatedHandler);
    this.destroyRef.onDestroy(() => window.removeEventListener('storage', issueUpdatedHandler));

    this.loadIssues();
    this.loadReviewPersonnel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reviewId'] && !changes['reviewId'].firstChange && changes['reviewId'].currentValue != null) {
      this.loadIssues();
      this.loadReviewPersonnel();
    }
  }

  // Data loaded for the Create New Issue modal.
  serviceLines: string[] = [];
  repLetterQuestions: string[] = [];
  riskCategories: any[] = [];
  locations: string[] = [];
  internalDependencies: string[] = [];
  reviewRcReviewer = '';
  reviewRcSupervisor = '';
  reviewRcManager = '';
  reviewSecondaryRcReviewer = '';
  reviewSecondaryRcSupervisor = '';
  reviewSecondaryRcManager = '';
  /** Issue ID value shown in the popup, from GetLastInsertedIssueByReviewID. */
  nextIssueId = '';
  /** Action Plan ID value shown in the popup, from GetLastInsertedActionPlanByReviewID. */
  nextActionPlanId = '';
  /** Existing action plans for this review (Step 2 list), from GetActionPlans. */
  actionPlans: any[] = [];

  // ── Issue Management grid ──
  /** In-memory linked-AP corrections indexed by issue ID — used when localStorage key mismatches. */
  private _linkedApFixes = new Map<string, string[]>();
  issues: any[] = [];
  showIssueGrid = true;
  issueSearch = '';
  issuePageSize = 10;
  issueCurrentPage = 1;
  issueGoToPageInput = 1;

  // ── Column Selector ──
  isColumnSelectorOpen = false;
  columnSelectorSearch = '';

  // ── Sorting ──
  sortColumn = 'IdentificationDate';
  sortDirection: 'asc' | 'desc' = 'desc';

  // ── Column Filters ──
  activeFilterColumn: string | null = null;
  operators = ['Contains', 'Not Contains', 'Equals', 'Starts With', 'Ends With'];
  filterableColumns = [
    'ReviewName', 'CurrentStatus', 'ProposedYear', 'MasterClientName', 'DTProjectName',
    'ContractNumber', 'AdditionalContractNumber', 'MarketUnit', 'CustomerRelationshipName',
    'PlanName', 'QADRecommendation', 'ClientQualityCQI', 'ClientAccountLeadGR',
    'OperationsAccountLeadOverall', 'MasterClientID', 'DeliveryCountry', 'LocationsToBeReviewed',
    'IsPrimaryLocation', 'Quarter', 'ProposedMonth', 'RCManager', 'RCSupervisor', 'RCReviewer',
    'SecondaryManager', 'SecondarySupervisor', 'SecondaryReviewer',
    'AccentureOperationsSL', 'TargetForReportPublications',
    'ActualPublicationDate', 'OverallReportingRating',
    'IssueID', 'IssueTitle', 'IssueDescription',
    'IssueRCReviewer', 'IssueRCSupervisor',
    'IssueRCManager', 'IdentificationDate', 'Subscribers', 'RCComments', 'ActionRequiredBy',
    'IssueRating', 'IssueStatus', 'LocationNames', 'RiskStatementL3', 'ReviewObjective',
    'Recommendation', 'BroadIssueCategoryL1', 'DetailedIssueCategoryL2', 'RepLetterConflict',
    'RepLetterQuestionsWithConflict', 'ServiceLine', 'LinkedActionPlan',
  ];
  filters: { [key: string]: ColumnFilter } = {};

  columnSections: ColumnSection[] = [
    {
      id: 'basicDetails',
      title: 'Review Basic Details',
      expanded: false,
      columns: [
        { key: 'ReviewName', label: 'Review Name', visible: false },
        { key: 'CurrentStatus', label: 'Current Status', visible: false },
        { key: 'ProposedYear', label: 'Proposed Year', visible: false },
        { key: 'MasterClientName', label: 'Master Client Name', visible: false },
        { key: 'DTProjectName', label: 'D&T Project Name', visible: false },
        { key: 'ContractNumber', label: 'Contract Number', visible: false },
        { key: 'AdditionalContractNumber', label: 'Additional Contract Number', visible: false },
        { key: 'MarketUnit', label: 'Market Unit', visible: false },
        { key: 'CustomerRelationshipName', label: 'Customer Relationship Name', visible: false },
        { key: 'PlanName', label: 'Plan Name', visible: false },
        { key: 'QADRecommendation', label: 'QAD Recommendation (Opp) / QAD Overall Status (Del)', visible: false },
        { key: 'ClientQualityCQI', label: 'Client Quality (CQI)', visible: false },
        { key: 'ClientAccountLeadGR', label: 'Client Account Lead (GR)', visible: false },
        { key: 'OperationsAccountLeadOverall', label: 'Operations Account Lead Overall', visible: false },
        { key: 'MasterClientID', label: 'Master Client ID', visible: false },
        { key: 'DeliveryCountry', label: 'Delivery Country', visible: false },
        { key: 'LocationsToBeReviewed', label: 'Locations to be Reviewed', visible: false },
        { key: 'IsPrimaryLocation', label: 'Is it Primary Location', visible: false },
        { key: 'Quarter', label: 'Quarter', visible: false },
        { key: 'ProposedMonth', label: 'Proposed Month', visible: false },
        { key: 'RCManager', label: 'R&C Manager', visible: false },
        { key: 'RCSupervisor', label: 'R&C Supervisor', visible: false },
        { key: 'RCReviewer', label: 'R&C Reviewer', visible: false },
        { key: 'SecondaryManager', label: 'Secondary Manager', visible: false },
        { key: 'SecondarySupervisor', label: 'Secondary Supervisor', visible: false },
        { key: 'SecondaryReviewer', label: 'Secondary Reviewer', visible: false },
      ],
    },
    {
      id: 'schedule',
      title: 'Schedule',
      expanded: false,
      columns: [
        { key: 'AccentureOperationsSL', label: 'Accenture Operation Service Line', visible: false },
        { key: 'TargetForReportPublications', label: 'Target for Report Publications', visible: false },
      ],
    },
    {
      id: 'reviewCycle',
      title: 'Review Cycle',
      expanded: false,
      columns: [
        { key: 'ActualPublicationDate', label: 'Actual Publication Date', visible: false },
        { key: 'OverallReportingRating', label: 'Overall Reporting Rating', visible: false },
      ],
    },
    {
      id: 'issueManagement',
      title: 'Issue Management',
      expanded: false,
      columns: [
        { key: 'IssueID', label: 'Issue ID', visible: true },
        { key: 'IssueTitle', label: 'Issue Title', visible: true },
        { key: 'IssueDescription', label: 'Issue Description', visible: true },
        { key: 'IssueRCReviewer', label: 'R&C Reviewer', visible: false },
        { key: 'IssueRCSupervisor', label: 'R&C Supervisor', visible: false },
        { key: 'IssueRCManager', label: 'R&C Manager', visible: false },
        { key: 'IdentificationDate', label: 'Identification Date', visible: true },
        { key: 'Subscribers', label: 'Subscribers/Notify', visible: false },
        { key: 'RCComments', label: 'R&C Comments', visible: false },
        { key: 'ActionRequiredBy', label: 'Action Required By', visible: false },
        { key: 'IssueRating', label: 'Issue Rating', visible: true },
        { key: 'IssueStatus', label: 'Issue Status', visible: true },
        { key: 'LocationNames', label: 'Location Reviewed and Impacted', visible: true },
        { key: 'RiskStatementL3', label: 'Risk Statement (L3)', visible: true },
        { key: 'LinkedActionPlan', label: 'Linked Action Plan', visible: true },
        { key: 'ReviewObjective', label: 'Review Objective', visible: false },
        { key: 'Recommendation', label: 'Recommendation', visible: false },
        { key: 'BroadIssueCategoryL1', label: 'Broad Issue Category (L1)', visible: false },
        { key: 'DetailedIssueCategoryL2', label: 'Detailed Issue Category (L2)', visible: false },
        { key: 'RepLetterConflict', label: 'RepLetter Conflict', visible: false },
        { key: 'RepLetterQuestionsWithConflict', label: 'RepLetter Questions with Conflict', visible: false },
        { key: 'ServiceLine', label: 'Accenture Operation Service Line', visible: false },
      ],
    },
  ];

  constructor(
    private reviewerService: ReviewerService,
    private rebarAuth: RebarAuthService,
    private router: Router,
    private userRoleService: UserRoleService,
    private bulkUploadValidation: BulkUploadValidationService,
  ) {
    this.filterableColumns.forEach(c => {
      this.filters[c] = { op1: 'Contains', val1: '' };
    });
  }

  /** The current user's application role name (e.g. 'Supervisor'), from UserRoleService. */
  get rollName(): string {
    return this.userRoleService.rollName;
  }

  /** True when the current user's application role is Supervisor. */
  get isSupervisor(): boolean {
    return this.userRoleService.isSupervisor;
  }

  get isReviewer(): boolean {
    return this.userRoleService.isReviewer;
  }

  get isAdmin(): boolean {
    return this.userRoleService.isAdmin;
  }

  get issueStatusMap(): Record<string, string> {
    const map: Record<string, string> = {};
    this.issues.forEach(i => {
      const id = String(i.IssueID ?? '').trim();
      if (id) map[`I-${id}`] = i.IssueStatus ?? '';
    });
    return map;
  }

  get isRCMember(): boolean {
    return this.userRoleService.isRCMember;
  }

  get isManager(): boolean {
    return this.userRoleService.isManager;
  }

  /** True when the signed-in user is named on this review's R&C personnel (primary or secondary). */
  get isAssignedToReview(): boolean {
    const toEid = (v: string | undefined | null) => (v ?? '').toLowerCase().split('@')[0];
    const userEid = toEid(this.userRoleService.enterpriseId) || toEid(this.userRoleService.email);
    if (!userEid) return false;
    return [
      this.reviewRcReviewer, this.reviewRcSupervisor, this.reviewRcManager,
      this.reviewSecondaryRcReviewer, this.reviewSecondaryRcSupervisor, this.reviewSecondaryRcManager,
    ].some(v => v && toEid(String(v)) === userEid);
  }

  // Admin can always upload; an R&C member may upload only when assigned to this review.
  get canUploadIssue(): boolean {
    return this.isAdmin || this.isAssignedToReview;
  }

  openUploadIssueModal(): void {
    this.bulkUploadErrors = [];
    this.uploadIssueModalOpen = true;
  }

  closeUploadIssueModal(): void {
    this.uploadIssueModalOpen = false;
  }

  async downloadIssueTemplate(): Promise<void> {
    if (this.isDownloadingIssueTemplate) return;

    this.isDownloadingIssueTemplate = true;

    try {
      const response = await fetch('/assets/documents/AST_Bulk_Upload_Template_V2_Dropdowns.xlsx');
      if (!response.ok) {
        throw new Error(`Failed to fetch template: ${response.status}`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'AST_Bulk_Upload_Template_V2_Dropdowns.xlsx';
      link.click();
      URL.revokeObjectURL(url);
      this.openUploadIssueToast('download', 'success');
    } catch (error) {
      console.error(error);
      this.openUploadIssueToast('download', 'error');
    } finally {
      this.isDownloadingIssueTemplate = false;
    }
  }

  async onBulkUploadFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.bulkUploadErrors = [];
    this.isUploadingIssueFile = true;
    const finishUpload = () => {
      this.isUploadingIssueFile = false;
    };

    try {
      const result = await this.bulkUploadValidation.validateBulkUploadFile(file, {
        reviewId: this.reviewId,
        userId: this.rebarAuth.getUserName() ?? '',
        reviewTypeId: this.reviewTypeId,
      });

      if (result.validation === 'failed') {
        this.bulkUploadErrors = result.errors ?? [];
        this.openUploadIssueToast('upload', 'error');
        finishUpload();
        return;
      }

      const payload = this.bulkUploadValidation.payload;
      if (!payload) {
        this.bulkUploadErrors = [{ error: 'Failed to prepare upload payload. Please try again.' }];
        this.openUploadIssueToast('upload', 'error');
        finishUpload();
        return;
      }

      console.log('Bulk upload payload (JSON):', JSON.stringify(payload, null, 2));

      this.reviewerService.bulkUploadIssue(payload)
        .pipe(timeout(30000))
        .subscribe({
          next: (response: any) => {
            console.log('Bulk upload API response:', response);
            const mappedServerErrors = this.bulkUploadValidation.normalizeBulkUploadApiErrors(response);
            const responseObj = Array.isArray(response) ? response[0] : response;
            const status = String(responseObj?.Status ?? responseObj?.status ?? '').toLowerCase();
            const hasSuccess = status === 'success' || responseObj?.Success === true || responseObj?.success === true;

            if (!hasSuccess && mappedServerErrors.length) {
              this.bulkUploadErrors = mappedServerErrors;
              this.openUploadIssueToast('upload', 'error');
              finishUpload();
              return;
            }

            if (!hasSuccess) {
              this.bulkUploadErrors = [{ error: 'Bulk upload validation failed from the server. Please review the response and retry.' }];
              this.openUploadIssueToast('upload', 'error');
              finishUpload();
              return;
            }

            this.openUploadIssueToast('upload', 'success', this.getBulkUploadSuccessMessage(response));
            finishUpload();
          },
          error: (error: any) => {
            console.error('Bulk upload API error:', error);
            this.bulkUploadErrors = [{ error: 'Bulk upload failed. Please try again.' }];
            this.openUploadIssueToast('upload', 'error');
            finishUpload();
          },
        });
    } catch (error) {
      console.error('Bulk upload process error:', error);
      this.bulkUploadErrors = [{ error: 'Bulk upload failed. Please try again.' }];
      this.openUploadIssueToast('upload', 'error');
      finishUpload();
    }
  }

  private getBulkUploadSuccessMessage(response: any): string {
    const responseObj = Array.isArray(response) ? response[0] : response;
    const issueCount = Number(responseObj?.TotalIssuesInserted ?? responseObj?.TotalIssues ?? responseObj?.issues ?? 0);
    const actionPlanCount = Number(responseObj?.TotalActionPlansInserted ?? responseObj?.TotalActionPlans ?? responseObj?.actionPlans ?? 0);
    return `${issueCount} Issues and ${actionPlanCount} Action Plans have been created in Draft status`;
  }

  openUploadIssueToast(action: UploadIssueToastAction, message: UploadIssueToastVariant, customContent?: string): void {
    const defaultMessages = this.uploadIssueToastMessages[action][message];
    const content = customContent ?? defaultMessages.content;
    const header = defaultMessages.header;
    const layout = this.getUploadToastLayout(action);

    const ref = this.dialog.open(this.uploadIssueToast(), {
      data: { title: message, header, content } satisfies UploadIssueToastData,
      width: `${layout.width}px`,
      height: `${layout.height}px`,
      panelClass: 'import-review-toast',
      hasBackdrop: false,
      position: layout.position,
      autoFocus: false,
    });
    this.uploadIssueToastRef = ref;
    this.activeUploadIssueToastAction = action;
    this.scheduleUploadIssueToastReposition();

    if (action === 'upload' && message === 'success') {
      setTimeout(() => window.location.reload(), 2000);
    }

    const autoCloseTimer = setTimeout(() => ref.close(), 10000);
    const unregisterOnDestroy = this.destroyRef.onDestroy(() => {
      clearTimeout(autoCloseTimer);
      ref.close();
    });

    ref.afterClosed().subscribe(() => {
      clearTimeout(autoCloseTimer);
      unregisterOnDestroy();
      if (this.uploadIssueToastRef === ref) {
        this.uploadIssueToastRef = null;
        this.activeUploadIssueToastAction = null;
      }
    });
  }

  private scheduleUploadIssueToastReposition(): void {
    // Recalculate after first paint and again after layout settles.
    requestAnimationFrame(() => this.repositionUploadIssueToast());
    setTimeout(() => this.repositionUploadIssueToast(), 0);
    setTimeout(() => this.repositionUploadIssueToast(), 120);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.repositionUploadIssueToast();
  }

  private repositionUploadIssueToast(): void {
    if (!this.uploadIssueToastRef || !this.activeUploadIssueToastAction) return;
    const layout = this.getUploadToastLayout(this.activeUploadIssueToastAction);
    this.uploadIssueToastRef.updateSize(`${layout.width}px`, `${layout.height}px`);
    this.uploadIssueToastRef.updatePosition(layout.position);
  }

  private getUploadToastLayout(action: UploadIssueToastAction): {
    width: number;
    height: number;
    position: { top: string; left?: string; right?: string };
  } {
    const width = action === 'download' ? 394 : 382;
    const height = action === 'download' ? 93 : 77;

    const popup = document.querySelector('.upload-modal') as HTMLElement | null;
    const rect = popup?.getBoundingClientRect();

    const top = rect ? Math.max(16, Math.floor(rect.top) + 8) : 120;
    const right = rect
      ? Math.max(16, Math.floor(window.innerWidth - rect.right) + 8)
      : 24;

    return {
      width,
      height,
      position: { top: `${top}px`, right: `${right}px` },
    };
  }

  get filteredIssues(): any[] {
    const term = this.issueSearch.trim().toLowerCase();
    const visibleKeys = this.columnSections
      .flatMap(s => s.columns)
      .filter(c => c.visible)
      .map(c => c.key);
    let list: any[] = term
      ? this.issues.filter((i) =>
          visibleKeys.some(col => String(i[col] ?? '').toLowerCase().includes(term)),
        )
      : [...this.issues];

    list = list.filter(row =>
      this.filterableColumns.every(col => {
        const f = this.filters[col];
        if (!f || !f.val1) return true;
        return this.matchOperator(String(row[col] ?? ''), f.op1, f.val1);
      }),
    );

    if (this.sortColumn) {
      const col = this.sortColumn;
      const dir = this.sortDirection;
      list.sort((a, b) => {
        let va: any = a[col];
        let vb: any = b[col];
        if (col === 'IdentificationDate' || col === 'ActualPublicationDate') {
          va = this.parseDateForSort(va);
          vb = this.parseDateForSort(vb);
        } else {
          if (typeof va === 'string') va = va.toLowerCase();
          if (typeof vb === 'string') vb = vb.toLowerCase();
        }
        if (va < vb) return dir === 'asc' ? -1 : 1;
        if (va > vb) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }

  get totalIssuePages(): number {
    return Math.max(1, Math.ceil(this.filteredIssues.length / this.issuePageSize));
  }

  get issuePageNumbers(): number[] {
    return Array.from({ length: this.totalIssuePages }, (_, i) => i + 1);
  }

  get pagedIssues(): any[] {
    const start = (this.issueCurrentPage - 1) * this.issuePageSize;
    return this.filteredIssues.slice(start, start + this.issuePageSize);
  }

  async exportVisible(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      const visibleKeys = new Set(
        this.columnSections.flatMap(section => section.columns)
          .filter(column => column.visible)
          .map(column => column.key),
      );
      await this.excelExportSvc.exportToExcel(
        this.exportableColumns().filter(column => visibleKeys.has(column.key)),
        this.filteredIssues,
        this.excelExportSvc.buildFileName('Issue_ExportVisible'),
        'Issue Export Visible',
      );
    } catch (error) {
      console.error('Issue Export Visible failed:', error);
    } finally {
      this.isExporting = false;
    }
  }

  async exportIssuesAndActionPlans(): Promise<void> {
    if (this.isExporting) return;
    this.isExporting = true;
    try {
      await this.excelExportSvc.exportToExcel(
        this.exportableColumns(),
        this.issues,
        this.excelExportSvc.buildFileName('Issues_ActionPlans_ExportAll'),
        'Issues and Action Plans',
      );
    } catch (error) {
      console.error('Issues and Action Plans export failed:', error);
    } finally {
      this.isExporting = false;
    }
  }

  private exportableColumns(): { key: string; header: string }[] {
    return this.columnSections
      .flatMap(section => section.columns)
      .map(column => ({ key: column.key, header: column.label }));
  }

  goToIssuePage(): void {
    const p = parseInt(String(this.issueGoToPageInput), 10);
    if (!isNaN(p) && p >= 1 && p <= this.totalIssuePages) {
      this.issueCurrentPage = p;
    }
  }

  toggleColumnSelector(): void {
    this.isColumnSelectorOpen = !this.isColumnSelectorOpen;
    if (this.isColumnSelectorOpen) {
      this.columnSelectorSearch = '';
    }
  }

  closeColumnSelector(): void {
    this.isColumnSelectorOpen = false;
  }

  toggleSection(section: ColumnSection): void {
    section.expanded = !section.expanded;
  }

  filteredSectionColumns(section: ColumnSection): ColumnItem[] {
    const term = this.columnSelectorSearch.trim().toLowerCase();
    if (!term) return section.columns;
    return section.columns.filter((c) => c.label.toLowerCase().includes(term));
  }

  isColumnVisible(key: string): boolean {
    for (const section of this.columnSections) {
      const col = section.columns.find((c) => c.key === key);
      if (col) return col.visible;
    }
    return true;
  }

  totalColumnCount(): number {
    return this.columnSections.reduce((sum, s) => sum + s.columns.length, 0);
  }

  visibleColumnCount(): number {
    return this.columnSections.reduce((sum, s) => sum + s.columns.filter(c => c.visible).length, 0);
  }

  sort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.issueCurrentPage = 1;
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  getSingleSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'arrow_drop_down';
    return this.sortDirection === 'asc' ? 'arrow_drop_up' : 'arrow_drop_down';
  }

  private parseDateForSort(dateStr: string): number {
    if (!dateStr) return 0;
    const MONTHS: Record<string, string> = {
      'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
      'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
      'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12',
    };
    const m = dateStr.match(/^(\d{2})-([A-Za-z]{3})-(\d{4})$/);
    if (m) {
      const month = MONTHS[m[2]] ?? '00';
      return parseInt(`${m[3]}${month}${m[1]}`, 10);
    }
    const m2 = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m2 ? parseInt(`${m2[3]}${m2[2]}${m2[1]}`, 10) : 0;
  }

  toggleColumnFilter(col: string, event: Event): void {
    event.stopPropagation();
    this.activeFilterColumn = this.activeFilterColumn === col ? null : col;
  }

  applyFilter(): void {
    this.activeFilterColumn = null;
    this.issueCurrentPage = 1;
  }

  resetFilter(col: string): void {
    this.filters[col] = { op1: 'Contains', val1: '' };
    this.issueCurrentPage = 1;
  }

  hasActiveFilter(col: string): boolean {
    const f = this.filters[col];
    return !!(f && f.val1);
  }

  private matchOperator(cell: string, op: string, val: string): boolean {
    if (!val) return true;
    const c = cell.toLowerCase();
    const v = val.toLowerCase();
    switch (op) {
      case 'Contains':     return c.includes(v);
      case 'Not Contains': return !c.includes(v);
      case 'Equals':       return c === v;
      case 'Starts With':  return c.startsWith(v);
      case 'Ends With':    return c.endsWith(v);
      default:             return true;
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: any): void {
    const clickedInsideTable = (event.target as HTMLElement).closest('table');
    if (!clickedInsideTable) {
      this.activeFilterColumn = null;
    }
  }

  setTooltipIfTruncated(event: MouseEvent): void {
    const el = event.currentTarget as HTMLElement;
    el.title = el.scrollWidth > el.clientWidth ? (el.textContent?.trim() ?? '') : '';
  }

  private loadReviewPersonnel(): void {
    if (this.reviewId == null) return;
    this.reviewerService.getReviewDetailsByID(this.reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rcRes: any) => {
          if (rcRes == null) return;
          const rows: any[] = Array.isArray(rcRes) ? rcRes : (rcRes?.value ?? rcRes?.recordset ?? [rcRes]);
          const d = rows.find((r: any) => {
            if (r?.IsParentLocation || r?.R_IsParentLocation) return true;
            const fArr: any[] = r?.Fields ?? [];
            return fArr.some((f: any) => f.FieldName === 'IsParentLocation' && f.FieldValue === '1');
          }) ?? rows[0] ?? {};
          const f: Record<string, string> = {};
          for (const field of (d.Fields ?? [])) { if (field.FieldName) f[field.FieldName] = field.FieldValue ?? ''; }
          this.reviewRcReviewer            = f['RCReviewer']              || d.R_RCReviewer             || d.RCReviewer          || '';
          this.reviewRcSupervisor          = f['RCSupervisor']           || d.R_RCSupervisor           || d.RCSupervisor        || '';
          this.reviewRcManager             = f['RCManager']              || d.R_RCManager              || d.RCManager           || '';
          this.reviewSecondaryRcReviewer   = f['secondaryRCReviewer']   || f['SecondaryRCReviewer']   || d.R_secondaryRCReviewer   || d.R_SecondaryRCReviewer  || d.SecondaryReviewer   || '';
          this.reviewSecondaryRcSupervisor = f['secondaryRCSupervisor'] || f['SecondaryRCSupervisor'] || d.R_secondaryRCSupervisor || d.R_SecondaryRCSupervisor || d.SecondarySupervisor || '';
          this.reviewSecondaryRcManager    = f['secondaryRCManager']    || f['SecondaryRCManager']    || d.R_secondaryRCManager    || d.R_SecondaryRCManager   || d.SecondaryManager    || '';
        },
      });
  }

  private loadIssues(): void {
    if (this.reviewId == null) return;
    this.isLoading = true;
    const issueObs = this.reviewerService.getIssueEAV(this.reviewId, null);
    issueObs
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const list = Array.isArray(response)
            ? response
            : (response?.issues ?? response?.Issues ?? response?.Issue ?? []);
          const persisted = (Array.isArray(list) ? list : []).map((i: any) => ({
            // Review Basic Details
            // EAV uses CurrentStatus directly; Standard uses Status
            ReviewName: i.ReviewName ?? '',
            CurrentStatus: i.Status ?? i.CurrentStatus ?? '',
            ProposedYear: i.ProposedYear ?? i.Review_ProposedYear ?? '',
            // EAV uses Review_ClientName; Standard uses ClientName
            MasterClientName: i.ClientName ?? i.Review_ClientName ?? '',
            DTProjectName: i.ProjectName ?? i['Review_D&TProjectName'] ?? i.Review_DTProjectName ?? i.Review_ProjectName ?? '',
            ContractNumber: i.ContractNumber ?? i.Review_ContractNumber ?? '',
            AdditionalContractNumber: i.AdditionalContractNumber ?? i.Review_AdditionalContractNumber ?? '',
            MarketUnit: i.MarketUnit ?? i.Review_MarketUnit ?? '',
            CustomerRelationshipName: i.CustomerRelationshipName ?? i.Review_CustomerRelationshipName ?? '',
            PlanName: i.PlanName ?? i.Review_PlanName ?? '',
            QADRecommendation: i.QADRecommendation ?? '',
            ClientQualityCQI: i.ClientQualityCQI ?? i.Review_ClientQualityCQI ?? '',
            ClientAccountLeadGR: i.ClientAccountLead ?? i.Review_ClientAccountLead ?? '',
            OperationsAccountLeadOverall: i.OperationAccountLead ?? i.Review_OperationAccountLead ?? '',
            MasterClientID: i.MasterClientID ?? i.Review_MasterClientID ?? '',
            DeliveryCountry: i.DeliveryCountry ?? i.Review_DeliveryCountry ?? '',
            LocationsToBeReviewed: i.Review_LocationName ?? i.Review_LocationToBeReviewed ?? '',
            IsPrimaryLocation: ((i.Review_IsParentLocation || i.IsParentLocation) === '1' || (i.Review_IsParentLocation || i.IsParentLocation) === 1) ? 'Yes' : 'No',
            Quarter: i.Review_Quarter ?? '',
            ProposedMonth: i.ProposedMonth ?? i.Review_ProposedMonth ?? '',
            RCManager: i.Review_RCManager ?? '',
            RCSupervisor: i.Review_RCSupervisor ?? '',
            RCReviewer: i.Review_RCReviewer ?? '',
            SecondaryManager: i.Review_secondaryRCManager ?? i.Review_SecondaryRCManager ?? i.Review_SecondaryManager ?? i.SecondaryRCManager ?? '',
            SecondarySupervisor: i.Review_secondaryRCSupervisor ?? i.Review_SecondaryRCSupervisor ?? i.Review_SecondarySupervisor ?? i.SecondaryRCSupervisor ?? '',
            SecondaryReviewer: i.Review_secondaryRCReviewer ?? i.Review_SecondaryRCReviewer ?? i.Review_SecondaryReviewer ?? i.SecondaryRCReviewer ?? '',
            // Schedule
            AccentureOperationsSL:  i.Schedule_AccentureOperationsServiceLine ?? i.Schedule_ServiceLine ?? '',
            TargetForReportPublications: this.toDisplayDate(i.Schedule_TargetPublicationDate ?? i.Schedule_TargetForReportPublication ?? i.TargetPublicationDate ?? ''),
            // Review Cycle
            ActualPublicationDate: this.toDisplayDate(i.ActualPublicationDate ?? ''),
            OverallReportingRating: i.OverallReportingRating ?? '',
            // Issue Management
            IssueID: i.IssueID ?? i.IssueId ?? '',
            IssueTitle: i.IssueTitle ?? '',
            IssueDescription: i.IssueDescription ?? '',
            IssueRCReviewer: i.RCReviewer || i.Review_RCReviewer || '',
            IssueRCSupervisor: i.RCSupervisor || i.Review_RCSupervisor || '',
            IssueRCManager: i.RCManager || i.Review_RCManager || '',
            IssueRCReviewerID: i.RCReviewerID ?? '',
            IssueRCSupervisorID: i.RCSupervisorID ?? '',
            IssueRCManagerID: i.RCManagerID ?? '',
            // EAV returns IdentificationDate already as "DD-Mon-YYYY"; Standard returns ISO — fall back to raw if toDisplayDate fails
            IdentificationDate: this.toDisplayDate(i.IdentificationDate ?? '') || (i.IdentificationDate ?? ''),
            Subscribers: (() => {
              const raw = i.SubscriberNames ?? i.Subscribers ?? i.IssueSubscribersList;
              if (Array.isArray(raw)) {
                return raw.map((s: any) => s.IssueSubscriber ?? s.EnterpriseId ?? s.Email ?? '').filter(Boolean).join(',');
              }
              return raw ?? '';
            })(),
            RCComments: i.RCComments ?? '',
            ActionRequiredBy: i.ActionRequiredBy ?? '',
            IssueRating: i.IssueRating ?? '',
            IssueStatus: i.IssueStatus ?? '',
            // EAV uses LocationNames; Standard uses Locations
            LocationNames: i.Locations ?? i.LocationNames ?? '',
            RiskStatementL3: i.RiskStatementL3 ?? '',
            // EAV uses LinkedActionPlanNames; Standard uses LinkedActions
            LinkedActionPlan: i.LinkedActions ?? i.LinkedActionPlanNames ?? '',
            ReviewObjective: i.ReviewObjective ?? '',
            Recommendation: i.Recommendation ?? '',
            BroadIssueCategoryL1: i.BroadIssueCategoryL1 ?? i.BroadIssueCategory ?? '',
            DetailedIssueCategoryL2: i.DetailedIssueCategoryL2 ?? i.DetailedIssueCategory ?? '',
            RepLetterConflict: (() => {
              const rid = String(i.IssueID ?? i.IssueId ?? '').replace(/^[A-Za-z]+-/i, '').trim();
              try { const c = localStorage.getItem(`ast_rlc_${rid}`); if (c !== null) return c; } catch {}
              const v = i.RepLetterConflict;
              if (v === true  || String(v).toLowerCase() === 'true' || v==='1'  || String(v).toLowerCase() === 'yes')           return 'Yes';
              if (v === false || String(v).toLowerCase() === 'false' || v==='0' || String(v).toLowerCase() === 'no')            return 'No';
              if (String(v).toLowerCase() === 'not applicable')                                                                   return 'Not Applicable';
              return v ?? '';
            })(),
            RepLetterQuestionsWithConflict: i['RepLetter Questions with Conflict'] ?? i.RepLetterQuestions ?? i.RepLetterQuestionText ?? '',
            ServiceLine: i.ServiceLine ?? i['Accenture Operation Service Line'] ?? '',
            IssueRepLetterQuarter: i.Quarter ?? '',
            IssueFinancialYear: i.FinancialYear ?? i.FinancialYearName ?? '',
            IssueMaxTargetDate: this.toDisplayDate(i.MaxTargetDate ?? i.IssueCloseDueDate ?? '') || (i.MaxTargetDate ?? i.IssueCloseDueDate ?? ''),
            IssueReportStatus: i.ReportStatus ?? '',
            IssueFromGoLive: i.IssueFromGoLive ?? '',
            LegacyIssueNumber: i.LegacyIssueNumber ?? '',
          }));
          // Enrich grid rows from localStorage — the list API may return incorrect linked AP data
          // and doesn't return SubscriberNames, so prefer the locally-cached source of truth.
          this.issues = persisted.map((issue: any) => {
            const id = String(issue.IssueID ?? '').replace(/^[A-Za-z]+-/i, '').trim();
            let enriched: any = issue;

            // Override LinkedActionPlan with what the user actually selected.
            // Priority: issue-view-edit state (UpdateActionPlanLink) → in-memory fix (UpsertIssueWithActionPlanEAV) → localStorage cache → API data.
            try {
              // Highest priority: state written by issue-view-edit after UpdateActionPlanLink save.
              const iveState = localStorage.getItem(`ast_ive_state_${id}`);
              if (iveState !== null) {
                const aps = JSON.parse(iveState);
                if (Array.isArray(aps)) {
                  const linkedApIds = aps
                    .filter((ap: any) => ap.linked)
                    .map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim())
                    .filter(Boolean);
                  // Sync into ast_linked_aps so subsequent reloads also use the updated value
                  try { localStorage.setItem(`ast_linked_aps_${id}`, JSON.stringify(linkedApIds)); } catch {}
                  try { localStorage.removeItem(`ast_ive_state_${id}`); } catch {}
                  this._linkedApFixes.set(id, linkedApIds);
                  enriched = { ...enriched, LinkedActionPlan: linkedApIds.map((apId: string) => 'AP-' + apId).join(',') };
                }
              } else {
                const lsLinked = localStorage.getItem(`ast_linked_aps_${id}`);
                if (this._linkedApFixes.has(id)) {
                  const fixIds = this._linkedApFixes.get(id)!;
                  enriched = { ...enriched, LinkedActionPlan: fixIds.map((apId: string) => 'AP-' + apId).join(',') };
                } else if (lsLinked !== null) {
                  const arr = JSON.parse(lsLinked);
                  if (Array.isArray(arr)) {
                    enriched = { ...enriched, LinkedActionPlan: arr.map((apId: string) => 'AP-' + apId).join(',') };
                  }
                }
              }
            } catch {}

            if (enriched.Subscribers) return enriched;
            const memSubs = this.issueSubscriberCache.get(id);
            if (memSubs?.length) return { ...enriched, Subscribers: memSubs.join(',') };
            try {
              const ls = localStorage.getItem(`ast_subs_${id}`);
              if (ls) {
                const arr = JSON.parse(ls);
                if (Array.isArray(arr) && arr.length) return { ...enriched, Subscribers: arr.join(',') };
              }
            } catch {}
            return enriched;
          });
          this.showIssueGrid = true;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('GetIssue error status:', err?.status, 'message:', err?.message);
          this.showIssueGrid = true;
          this.isLoading = false;
        },
      });
  }

  navigateToIssue(issueId: string | number, mode: 'view' | 'edit' = 'view', fromViewIcon = false): void {
    const isGoLive = this.reviewTypeId === 2;
    const urlTree = this.router.createUrlTree(['/issue-management', `I-${issueId}`], {
      queryParams: { mode, reviewId: this.reviewId, isGoLive: isGoLive ? '1' : '0', fromViewIcon: fromViewIcon ? '1' : '0' }
    });
    window.open(this.router.serializeUrl(urlTree), '_blank');
  }

  onEditIssue(issue: any): void {
    const status = issue.IssueStatus?.toLowerCase().replace(/[^a-z]/g, '');
    console.log('[onEditIssue]', {
      IssueID: issue.IssueID,
      IssueStatus: issue.IssueStatus,
      statusNormalized: status,
      isSupervisor: this.isSupervisor,
      isManager: this.userRoleService.isManager,
      SecondaryRCSupervisor: issue.SecondaryRCSupervisor,
    });
    if ((this.isSupervisor || this.isUserSupervisorOrManager(issue)) && status === 'rejected') {
      this.navigateToIssue(issue.IssueID, 'edit');
    } else if (status === 'draft' || status === 'rejected') {
      this.openEditIssueModal(issue);
    } else {
      const toEid = (v: string | undefined | null) => (v ?? '').toLowerCase().split('@')[0];
      const userEid = (this.userRoleService.enterpriseId ?? '').toLowerCase() || toEid(this.userRoleService.email);
      const assignedFields = [
        issue.RCSupervisor, issue.Review_RCSupervisor,
        issue.SecondaryRCSupervisor, issue.Review_SecondaryRCSupervisor,
        issue.SecondarySupervisor,
        issue.RCManager, issue.Review_RCManager,
        issue.RCReviewer, issue.Review_RCReviewer,
      ];
      const isAssigned = !!userEid && assignedFields.some(v => v && toEid(String(v)) === userEid);
      const canEdit = this.userRoleService.isAdmin || isAssigned;
      console.log('[onEditIssue] canEdit:', canEdit, '→ navigating to', canEdit ? 'edit' : 'view');
      this.navigateToIssue(issue.IssueID, canEdit ? 'edit' : 'view');
    }
  }

  isIssueEditable(issue: any): boolean {
    return issue.IssueStatus?.toLowerCase().replace(/[^a-z]/g, '') === 'inactive';
  }

  private isUserSupervisorOrManager(issue: any): boolean {
    const toEid = (v: string | undefined | null) => (v ?? '').toLowerCase().split('@')[0];
    const userEid = (this.userRoleService.enterpriseId ?? '').toLowerCase() || toEid(this.userRoleService.email);
    if (!userEid) return false;
    const fields = [
      issue.RCSupervisor, issue.Review_RCSupervisor,
      issue.SecondaryRCSupervisor, issue.Review_SecondaryRCSupervisor,
      issue.SecondarySupervisor,
      issue.RCManager, issue.Review_RCManager,
    ];
    return fields.some(v => v && toEid(String(v)) === userEid);
  }

  openAddIssueModal() {
    this.isLoading = true;
    this.loadRepLetterQuestions();
    this.loadRiskCategories();
    this.loadLocations();
    this.loadNextIssueId();
    this.loadNextActionPlanId();
    this.loadActionPlans();
    this.loadInternalDependencies();

    const rcPersonnel$ = this.reviewId != null
      ? this.reviewerService.getReviewDetailsByID(this.reviewId)
      : of(null);

    forkJoin([this.reviewerService.getServiceLines(), rcPersonnel$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([slRes, rcRes]: [any, any]) => {
          if (Array.isArray(slRes)) {
            const lines: string[] = slRes
              .map((item: any) => item.ServiceLineName ?? item.Name ?? item)
              .filter((v: any) => typeof v === 'string' && v.trim().length > 0);
            if (lines.length) this.serviceLines = lines;
          }
          if (rcRes != null) {
            const rows: any[] = Array.isArray(rcRes) ? rcRes : (rcRes?.value ?? rcRes?.recordset ?? [rcRes]);
            const d = rows.find((r: any) => {
              if (r?.IsParentLocation || r?.R_IsParentLocation) return true;
              const fArr: any[] = r?.Fields ?? [];
              return fArr.some((f: any) => f.FieldName === 'IsParentLocation' && f.FieldValue === '1');
            }) ?? rows[0] ?? {};
            const f: Record<string, string> = {};
            for (const field of (d.Fields ?? [])) { if (field.FieldName) f[field.FieldName] = field.FieldValue ?? ''; }
            this.reviewRcReviewer            = f['RCReviewer']              || d.R_RCReviewer             || d.RCReviewer          || '';
            this.reviewRcSupervisor          = f['RCSupervisor']           || d.R_RCSupervisor           || d.RCSupervisor        || '';
            this.reviewRcManager             = f['RCManager']              || d.R_RCManager              || d.RCManager           || '';
            this.reviewSecondaryRcReviewer   = f['secondaryRCReviewer']   || f['SecondaryRCReviewer']   || d.R_secondaryRCReviewer   || d.R_SecondaryRCReviewer  || d.SecondaryReviewer   || '';
            this.reviewSecondaryRcSupervisor = f['secondaryRCSupervisor'] || f['SecondaryRCSupervisor'] || d.R_secondaryRCSupervisor || d.R_SecondaryRCSupervisor || d.SecondarySupervisor || '';
            this.reviewSecondaryRcManager    = f['secondaryRCManager']    || f['SecondaryRCManager']    || d.R_secondaryRCManager    || d.R_SecondaryRCManager   || d.SecondaryManager    || '';
          }
          this.isLoading = false;
          this.addIssueModalOpen = true;
        },
        error: () => { this.isLoading = false; this.addIssueModalOpen = true; },
      });
  }

  editingIssueDocuments: any[] = [];
  private issueSubscriberCache = new Map<string, string[]>();

  showDeleteModal = false;
  isDeletingIssue = false;
  private pendingDeleteIssue: any = null;

  openDeleteModal(issue: any): void {
    if (!this.isAdmin) return;
    this.pendingDeleteIssue = issue;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.pendingDeleteIssue = null;
  }

  closeSuccessToast(): void {
    this.successToastMessage = null;
    document.body.style.overflow = '';
  }

  confirmDeleteIssue(): void {
    if (!this.pendingDeleteIssue || this.isDeletingIssue) return;
    this.showDeleteModal = false;
    this.isDeletingIssue = true;
    const issue = this.pendingDeleteIssue;
    const loggedUserId = this.userRoleService.enterpriseId || (this.userRoleService.email ?? '').split('@')[0];
    const issueId = Number(String(issue.IssueID).replace(/^[A-Za-z]+-/i, '').trim());
    this.reviewerService.deleteIssue(this.reviewId!, issueId, loggedUserId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.issues = this.issues.filter(i => i !== issue);
          this.isDeletingIssue = false;
          this.pendingDeleteIssue = null;
          this.successToastMessage = 'Issue deleted successfully';
          document.body.style.overflow = 'hidden';
          setTimeout(() => { this.successToastMessage = null; document.body.style.overflow = ''; }, 5000);
        },
        error: (err) => {
          console.error('Delete issue error:', err);
          this.isDeletingIssue = false;
          this.pendingDeleteIssue = null;
        },
      });
  }

  closeAddIssueModal() {
    this.addIssueModalOpen = false;
    this.editingIssueData = null;
    this.editingIssueDocuments = [];
  }

  openEditIssueModal(issue: any) {
    const editableStatuses = ['Draft', 'Rejected'];
    if (!editableStatuses.includes(issue.IssueStatus)) return;

    // Restore cached subscribers (API does not return them in the list response).
    let cachedSubscribers = '';
    let subsCacheFound = false;
    const normalizedIssueId = String(issue.IssueID).replace(/^[A-Za-z]+-/i, '').trim();
    // Memory cache (same-session, most reliable) takes priority over localStorage.
    const memorySubs = this.issueSubscriberCache.get(normalizedIssueId);
    if (memorySubs !== undefined) {
      // Treat empty array as a valid "cleared" state — do not fall back to stale grid value.
      cachedSubscribers = memorySubs.join(',');
      subsCacheFound = true;
    } else {
      try {
        const cached = localStorage.getItem(`ast_subs_${normalizedIssueId}`);
        if (cached !== null) {
          const arr = JSON.parse(cached);
          cachedSubscribers = Array.isArray(arr) ? arr.join(',') : String(arr);
          subsCacheFound = true;
        }
      } catch { /* ignore storage errors */ }
    }

    // Restore cached linked action plan IDs saved by onSaveIssueDraft.
    // null = no cache written yet (first open); '' = cache written but empty (user selected none).
    let cachedLinkedAps: string | null = null;
    try {
      const lsLinked = localStorage.getItem(`ast_linked_aps_${normalizedIssueId}`);
      if (lsLinked !== null) {
        const arr = JSON.parse(lsLinked);
        cachedLinkedAps = Array.isArray(arr) ? arr.join(',') : String(arr);
      }
    } catch {}

    let cachedRlc: string | null = null;
    try { cachedRlc = localStorage.getItem(`ast_rlc_${normalizedIssueId}`); } catch {}

    console.log('[openEditIssueModal] normalizedIssueId:', normalizedIssueId, '| cachedLinkedAps:', cachedLinkedAps);

    this.editingIssueData = { ...issue, Subscribers: subsCacheFound ? cachedSubscribers : (issue.Subscribers || '') };
    this.editingIssueDocuments = [];
    const issueId = Number(issue.IssueID);
    this.isLoading = true;
    this.reviewerService.getIssueEAV(this.reviewId, issueId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          // Handle single-object, array, or nested-wrapper response shapes.
          const raw = Array.isArray(res)
            ? res[0]
            : (res?.issues?.[0] ?? res?.Issues?.[0] ?? res?.Issue?.[0] ?? res);
          if (raw && typeof raw === 'object') {
            // Parse existing document attachments. API returns the field as "Documents".
            try {
              const docsField = raw.IssueDocumentsList ?? raw.Documents ?? raw.DocumentsJSON;
              const docs = docsField
                ? (typeof docsField === 'string' ? JSON.parse(docsField) : docsField)
                : [];
              this.editingIssueDocuments = Array.isArray(docs) ? docs : [];
            } catch { this.editingIssueDocuments = []; }
            // If API returned no documents, fall back to localStorage cache.
            if (!this.editingIssueDocuments.length) {
              try {
                const cachedDocs = localStorage.getItem(`ast_docs_${issueId}`);
                if (cachedDocs) {
                  const arr = JSON.parse(cachedDocs);
                  this.editingIssueDocuments = Array.isArray(arr) ? arr : [];
                }
              } catch {}
            }
            // Merge subscriber data: API response takes priority; cache (even if empty) overrides stale grid value.
            const rawApiSubs = raw.Subscribers || raw.SubscriberNames || raw.NotifyEmails || raw.Notify || '';
            const apiSubs = Array.isArray(rawApiSubs)
              ? rawApiSubs.map((s: any) => s.IssueSubscriber ?? s.EnterpriseId ?? s.Email ?? '').filter(Boolean).join(',')
              : rawApiSubs;
            const alreadySet = this.editingIssueData?.Subscribers || '';
            const finalSubs = subsCacheFound ? (apiSubs || cachedSubscribers) : (apiSubs || cachedSubscribers || issue.Subscribers || alreadySet || '');
            const apiRepLetterQ = raw['RepLetter Questions with Conflict'] ?? raw.RepLetterQuestions ?? raw.RepLetterQuestionText ?? raw.RepLetterQuestionsWithConflict ?? issue.RepLetterQuestionsWithConflict ?? '';
            // Extract linked AP IDs from the API response, handling string, number-array,
            // and object-array shapes — String([{...}]) produces "[object Object]" which is wrong.
            const rawLinked = raw.LinkedActions ?? raw.LinkedActionPlan ?? raw.LinkedActionPlans;
            let apiLinkedAps = '';
            if (rawLinked) {
              if (Array.isArray(rawLinked)) {
                apiLinkedAps = rawLinked.map((item: any) =>
                  typeof item === 'string' || typeof item === 'number'
                    ? String(item).trim()
                    : String(item.ActionPlanID ?? item.ActionPlanId ?? item.ID ?? item.Id ?? '').trim()
                ).filter(Boolean).join(',');
              } else {
                apiLinkedAps = String(rawLinked).trim();
              }
            }
            // Prefer the localStorage cache (what the user explicitly chose) over the API value.
            // The backend stored procedure may incorrectly link all existing APs, so when a cache
            // entry exists (even as empty ''), it is the authoritative source.
            const finalLinkedAps = cachedLinkedAps !== null
              ? cachedLinkedAps
              : (apiLinkedAps || String(issue.LinkedActionPlan ?? ''));
            this.editingIssueData = { ...issue, Subscribers: finalSubs, RepLetterQuestionsWithConflict: apiRepLetterQ, LinkedActionPlan: finalLinkedAps, ...(cachedRlc !== null ? { RepLetterConflict: cachedRlc } : {}) };
          }
          this.isLoading = false;
          this.openAddIssueModal();
        },
        error: () => {
          // API failed — still try localStorage for documents.
          try {
            const cachedDocs = localStorage.getItem(`ast_docs_${issueId}`);
            if (cachedDocs) {
              const arr = JSON.parse(cachedDocs);
              this.editingIssueDocuments = Array.isArray(arr) ? arr : [];
            }
          } catch {}
          if (cachedLinkedAps) this.editingIssueData = { ...this.editingIssueData, LinkedActionPlan: cachedLinkedAps };
          if (cachedRlc !== null) this.editingIssueData = { ...this.editingIssueData, RepLetterConflict: cachedRlc };
          this.isLoading = false;
          this.openAddIssueModal();
        },
      });
  }

  async onSaveIssueDraft(payload: { step: 1 | 2; form: any; files: any[]; actionPlans: any[]; newActionPlans: any[]; newActionPlanFiles: any[][]; deletedFilePaths?: string[] }): Promise<void> {
    if (this.reviewTypeId === 2) {
      await this.submitIssueGoLive(payload, 'Draft');
      return;
    }
    // Strip display prefix ("I-357" → "357") so the key matches the raw IssueID used on load.
    const rawId = String(payload.form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();

    if (rawId) {
      try { localStorage.setItem(`ast_subs_${rawId}`, JSON.stringify(payload.form.subscribers ?? [])); } catch {}
      // Cache the RepLetterConflict string so "Not Applicable" survives the boolean round-trip.
      try { localStorage.setItem(`ast_rlc_${rawId}`, payload.form.repLetterConflict ?? ''); } catch {}
      // Cache linked AP IDs so popup-2 can pre-check the right boxes on next edit.
      // Include both existing linked APs and newly created AP display IDs (e.g. AP-401)
      // so the grid shows the linked AP immediately without waiting for the next API refresh.
      try {
        const existingApIds = (payload.actionPlans ?? [])
          .map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim())
          .filter(Boolean);
        const newApIds = (payload.newActionPlans ?? [])
          .map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim())
          .filter(Boolean);
        const apIds = [...new Set([...existingApIds, ...newApIds])];
        localStorage.setItem(`ast_linked_aps_${rawId}`, JSON.stringify(apIds));
        this._linkedApFixes.set(rawId, apIds); // also store in-memory as fallback
      } catch {}
      // Cache file metadata so edit mode can display previously attached files.
      try {
        const prev: any[] = (() => { try { return JSON.parse(localStorage.getItem(`ast_docs_${rawId}`) ?? '[]'); } catch { return []; } })();
        const deleted = new Set<string>(payload.deletedFilePaths ?? []);
        const kept = prev.filter((f: any) => !deleted.has(f.StorageFilePath));
        const added = (payload.files as File[]).map((f: File) => ({
          FileName: f.name,
          StorageFilePath: `${this.reviewId}/${payload.form.issueId}/${f.name}`,
          FileSize: f.size,
          FileType: f.name.split('.').pop() ?? '',
        }));
        localStorage.setItem(`ast_docs_${rawId}`, JSON.stringify([...kept, ...added]));
      } catch {}
    }
    await this.submitIssue(payload, 'Draft', payload.form.reportStatus ?? '');
  }

  async onSendForApproval(payload: { step: 1 | 2; form: any; files: any[]; actionPlans: any[]; newActionPlans: any[]; newActionPlanFiles: any[][]; deletedFilePaths?: string[] }): Promise<void> {
    if (this.reviewTypeId === 2) {
      await this.submitIssueGoLive(payload, 'Inactive');
      return;
    }
    const rawId = String(payload.form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
    if (rawId) {
      try { localStorage.setItem(`ast_subs_${rawId}`, JSON.stringify(payload.form.subscribers ?? [])); } catch {}
      try {
        const prev: any[] = (() => { try { return JSON.parse(localStorage.getItem(`ast_docs_${rawId}`) ?? '[]'); } catch { return []; } })();
        const deleted = new Set<string>(payload.deletedFilePaths ?? []);
        const kept = prev.filter((f: any) => !deleted.has(f.StorageFilePath));
        const added = (payload.files as File[]).map((f: File) => ({
          FileName: f.name,
          StorageFilePath: `${this.reviewId}/${payload.form.issueId}/${f.name}`,
          FileSize: f.size,
          FileType: f.name.split('.').pop() ?? '',
        }));
        localStorage.setItem(`ast_docs_${rawId}`, JSON.stringify([...kept, ...added]));
      } catch {}
    }
    const actionType = 'Inactive';
    await this.submitIssue(payload, actionType, payload.form.reportStatus ?? '');
  }

  private async submitIssue(
    payload: { step: 1 | 2; form: any; files: any[]; actionPlans: any[]; newActionPlans: any[]; newActionPlanFiles: any[][] },
    actionType: string,
    reportStatus: string,
  ): Promise<void> {
    const form = payload.form;
    const files = payload.files ?? [];

    // Capture IssueID synchronously before any await — closeAddIssueModal() resets
    // editingIssueData to null before async continuations resume, so we must read it now.
    const issueIdForUpdate = (() => {
      if (!this.editingIssueData) return null;
      const id = String(form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
      return id || null;
    })();

    this.addIssueModalOpen = false;
    this.isLoading = true;

    // ── Step 1: Issue documents ──
    const documents = await Promise.all(
      files.map(async (file: File) => {
        const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
        const fileContent = await this.readFileAsBase64(file);
        return {
          FileName: file.name,
          StorageFilePath: `${this.reviewId}/${form.issueId}/${file.name}`,
          fileContent,
          DocumentType: '',
          FileSize: file.size,
          FileType: ext,
          Description: '',
          UploadedBy: this.rebarAuth.getUserName() ?? '',
        };
      }),
    );

    // IssueFieldsJSON EAV array
    const repLetterYes = form.repLetterConflict === 'Yes';
    const issueFields = [
      { FieldName: 'IssueTitle',              FieldValue: form.issueTitle ?? '',                                                                                                   DataType: 'NVARCHAR' },
      { FieldName: 'IssueDescription',        FieldValue: form.issueDescription ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'ActionRequiredBy',        FieldValue: form.actionRequiredBy ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'BroadIssueCategoryL1',    FieldValue: form.broadCategory ?? '',                                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'DetailedIssueCategoryL2', FieldValue: form.detailedCategory ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'RiskStatementL3',         FieldValue: form.riskStatement ?? '',                                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'FinancialYear',           FieldValue: this.toFinancialYearId(form.financialYear) ?? '',                                                                        DataType: 'NVARCHAR' },
      { FieldName: 'Quarter',                 FieldValue: form.quarter ?? '',                                                                                                      DataType: 'NVARCHAR' },
      { FieldName: 'IdentificationDate',      FieldValue: this.toDisplayDate(form.identificationDate),                                                                            DataType: 'DATE'     },
      ...(form.issueCloseDueDate               ? [{ FieldName: 'IssueCloseDueDate',       FieldValue: form.issueCloseDueDate,                                                    DataType: 'DATE' }] : []),
      { FieldName: 'LegacyIssueNumber',      FieldValue: form.legacyIssueNumber ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'IssueRating',             FieldValue: form.issueRating ?? '',                                                                                                 DataType: 'NVARCHAR' },
      { FieldName: 'ReportStatus',            FieldValue: reportStatus,                                                                                                           DataType: 'NVARCHAR' },
      { FieldName: 'RepLetterConflict',       FieldValue: form.repLetterConflict === 'Yes' ? 'Yes' : form.repLetterConflict === 'No' ? 'No' : 'Not Applicable',                    DataType: 'NVARCHAR' },
      { FieldName: 'RepLetterQuestions',      FieldValue: repLetterYes && form.repLetterQuestionsWithConflict?.length ? form.repLetterQuestionsWithConflict.join(',') : 'N/A',    DataType: 'NVARCHAR' },
      { FieldName: 'Recommendation',          FieldValue: form.recommendation ?? '',                                                                                              DataType: 'NVARCHAR' },
      { FieldName: 'ReviewObjective',         FieldValue: form.reviewObjective ?? '',                                                                                             DataType: 'NVARCHAR' },
      { FieldName: 'ServiceLine',             FieldValue: (form.serviceLines ?? []).join(','),                                                                                    DataType: 'NVARCHAR' },
      { FieldName: 'RCManager',               FieldValue: form.rcManager ?? '',                                                                                                   DataType: 'NVARCHAR' },
      { FieldName: 'RCReviewer',              FieldValue: form.rcReviewer ?? '',                                                                                                  DataType: 'NVARCHAR' },
      { FieldName: 'RCSupervisor',            FieldValue: form.rcSupervisor ?? '',                                                                                                DataType: 'NVARCHAR' },
      { FieldName: 'RCComments',              FieldValue: form.rcCommentsForIssue ?? '',                                                                                          DataType: 'NVARCHAR' },
      { FieldName: 'LocationReviewedImpacted',FieldValue: (form.locationsImpacted ?? []).join(','),                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'LocationNames',           FieldValue: (form.locationsImpacted ?? []).join(','),                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'SubscriberNames',         FieldValue: (form.subscribers ?? []).join(','),                                                                                     DataType: 'NVARCHAR' },
      { FieldName: 'DocumentsJSON',           FieldValue: documents.length ? JSON.stringify(documents) : '[]',                                                                    DataType: 'NVARCHAR' },
    ];

    // ActionPlansJSON — existing linked APs + new APs
    const actionPlansJson: any[] = [];

    for (const ap of (payload.actionPlans ?? [])) {
      const numericId = String(ap.id ?? '').replace(/^AP-/i, '').trim();
      const orig: any = this.actionPlans.find((a: any) => {
        const rawId = String(
          a.ActionPlanID ?? a.ActionPlanId ?? a.ID ?? a.Id ?? a.ActionID ?? '',
        ).trim().replace(/^AP-/i, '');
        return rawId === numericId;
      }) ?? {};

      actionPlansJson.push({
        ActionPlanID:   numericId ? Number(numericId) : null,
        ActionPlanName: ap.id ?? '',
        ReviewID:       orig.ReviewID ?? this.reviewId,
        Fields: [
          { FieldName: 'AP_Description',        FieldValue: orig.AP_Description ?? orig.Description ?? orig.ActionPlanDescription ?? '',                              DataType: 'NVARCHAR' },
          { FieldName: 'AP_OwnerEmail',          FieldValue: orig.AP_OwnerEmail ?? orig.OwnerEmail ?? '',                                                             DataType: 'NVARCHAR' },
          ...(orig.AP_TargetDate ?? orig.TargetDateForActionPlanRemediationAction ?? orig.TargetDate ? [{ FieldName: 'AP_TargetDate', FieldValue: String(orig.AP_TargetDate ?? orig.TargetDateForActionPlanRemediationAction ?? orig.TargetDate).split('T')[0], DataType: 'DATE' }] : []),
          { FieldName: 'AP_Status',              FieldValue: orig.AP_Status ?? orig.Status ?? orig.ActionPlanStatus ?? '',                                            DataType: 'NVARCHAR' },
          { FieldName: 'AP_InternalDependency',  FieldValue: orig.AP_InternalDependency ?? orig.InternalDependency ?? '',                                             DataType: 'NVARCHAR' },
          { FieldName: 'AP_ClientDependency',    FieldValue: orig.AP_ClientDependency ?? orig.ClientDependency ?? '',                                                 DataType: 'NVARCHAR' },
          ...(orig.AP_ClosedDate ?? orig.ClosedDate ? [{ FieldName: 'AP_ClosedDate', FieldValue: String(orig.AP_ClosedDate ?? orig.ClosedDate).split('T')[0], DataType: 'DATE' }] : []),
          ...(orig.AP_RevisedTargetDate ?? orig.RevisedTargetDate ? [{ FieldName: 'AP_RevisedTargetDate', FieldValue: String(orig.AP_RevisedTargetDate ?? orig.RevisedTargetDate).split('T')[0], DataType: 'DATE' }] : []),
          { FieldName: 'DocumentsJSON',          FieldValue: '[]',                                                                                                   DataType: 'NVARCHAR' },
        ],
      });
    }

    const newAps: any[] = payload.newActionPlans ?? [];
    const newApFilesArray: any[][] = payload.newActionPlanFiles ?? [];
    for (let apIdx = 0; apIdx < newAps.length; apIdx++) {
      const newAp = newAps[apIdx];
      const newApFiles: any[] = newApFilesArray[apIdx] ?? [];
      const newApOwnerEmail = (newAp.owners ?? []).map((o: string) => o.includes('@') ? o : `${o}@accenture.com`).join(';');
      const newApDocuments = await Promise.all(
        newApFiles.map(async (file: File) => {
          const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
          const fileContent = await this.readFileAsBase64(file);
          return {
            FileName: file.name,
            StorageFilePath: `${this.reviewId}/${form.issueId}/${newAp.id}/${file.name}`,
            fileContent,
            DocumentType: '',
            FileSize: file.size,
            FileType: ext,
            Description: '',
            UploadedBy: this.rebarAuth.getUserName() ?? '',
          };
        }),
      );
      actionPlansJson.push({
        ActionPlanID:   null,
        ActionPlanName: null,
        ReviewID:       this.reviewId,
        Fields: [
          { FieldName: 'AP_Description',       FieldValue: newAp.description ?? '',                                                                                                                    DataType: 'NVARCHAR' },
          { FieldName: 'AP_OwnerEmail',         FieldValue: newApOwnerEmail,                                                                                                                            DataType: 'NVARCHAR' },
          ...(newAp.targetDate        ? [{ FieldName: 'AP_TargetDate',        FieldValue: newAp.targetDate,        DataType: 'DATE' }] : []),
          { FieldName: 'AP_Status',             FieldValue: newAp.status?.trim() || '',                           DataType: 'NVARCHAR' },
          { FieldName: 'AP_InternalDependency', FieldValue: (newAp.internalDependencies ?? []).join(','),                                                                                               DataType: 'NVARCHAR' },
          { FieldName: 'AP_ClientDependency',   FieldValue: newAp.clientDependency ?? '',                                                                                                               DataType: 'NVARCHAR' },
          ...(newAp.closedDate        ? [{ FieldName: 'AP_ClosedDate',        FieldValue: newAp.closedDate,        DataType: 'DATE' }] : []),
          ...(newAp.revisedTargetDate ? [{ FieldName: 'AP_RevisedTargetDate', FieldValue: newAp.revisedTargetDate, DataType: 'DATE' }] : []),
          { FieldName: 'LegacyAPNumber',        FieldValue: newAp.legacyApNumber ?? '',                                                                                                                 DataType: 'NVARCHAR' },
          { FieldName: 'AP_RCComments',         FieldValue: newAp.rcComments ?? '',                                                                                                                    DataType: 'NVARCHAR' },
          ...(newAp.redClientInformedDate        ? [{ FieldName: 'AP_RedClientDate',     FieldValue: newAp.redClientInformedDate,       DataType: 'DATE' }] : []),
          ...(newAp.targetDateToInformRedClient  ? [{ FieldName: 'TargetDateclientinfo', FieldValue: newAp.targetDateToInformRedClient, DataType: 'DATE' }] : []),
          { FieldName: 'DocumentsJSON',         FieldValue: newApDocuments.length ? JSON.stringify(newApDocuments) : '[]',                                                                              DataType: 'NVARCHAR' },
        ],
      });
    }

    const eavParams = {
      category:        'UpsertIssueWithActionPlan_EAV',
      IssueID:         issueIdForUpdate,
      ReviewID:        this.reviewId,
      ActionPlanID:    null,
      IssueName:       form.issueTitle,
      UserID:          this.rebarAuth.getUserName() ?? '',
      ActionType:      actionType,
      IssueFieldsJSON: JSON.stringify(issueFields),
      ActionPlansJSON: JSON.stringify(actionPlansJson),
    };

    this.reviewerService.UpsertIssueWithActionPlanEAV(eavParams)
      .pipe(timeout(30000))
      .subscribe({
        next: (response: any) => {
          const formCacheId = String(form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
          const responseObj = Array.isArray(response) ? response[0] : response;
          const responseCacheId = String(
            responseObj?.NewIssueID ?? responseObj?.newIssueId ?? responseObj?.IssueID ?? responseObj?.issueId ?? responseObj?.IssueId ?? responseObj?.data?.IssueID ?? ''
          ).replace(/^[A-Za-z]+-/i, '').trim();
          const subs: string[] = form.subscribers ?? [];
          console.log('[CREATE next] formCacheId:', formCacheId, '| responseCacheId:', responseCacheId, '| response:', JSON.stringify(response), '| subs:', subs);
          const existingApIds = (payload.actionPlans ?? [])
            .map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim())
            .filter(Boolean);
          const newApIds = (payload.newActionPlans ?? [])
            .map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim())
            .filter(Boolean);
          const apIds = [...new Set([...existingApIds, ...newApIds])];
          for (const id of Array.from(new Set([formCacheId, responseCacheId].filter(k => k)))) {
            this.issueSubscriberCache.set(id, [...subs]);
            try { localStorage.setItem(`ast_subs_${id}`, JSON.stringify(subs)); } catch {}
            try { localStorage.setItem(`ast_linked_aps_${id}`, JSON.stringify(apIds)); } catch {}
            this._linkedApFixes.set(id, apIds);
          }
          const doFinish = () => {
            this.draftSavedMessage = true;
            setTimeout(() => (this.draftSavedMessage = false), 3000);
            this.showIssueGrid = true;
            this.loadIssues();
            this.reviewerService.notifyActionPlanSaved();
          };

          const newIssueId = Number(responseCacheId) || Number(formCacheId);
          doFinish();
        },
        error: (err) => {
          console.error('UpsertIssueWithActionPlan_EAV error status:', err?.status, 'message:', err?.message);
          this.isLoading = false;
        },
      });
  }

  private async submitIssueGoLive(
    payload: { step: 1 | 2; form: any; files: any[]; actionPlans: any[]; newActionPlans: any[]; newActionPlanFiles: any[][] },
    actionType: string,
  ): Promise<void> {
    const form = payload.form;
    const files = payload.files ?? [];

    const issueIdForUpdate = (() => {
      if (!this.editingIssueData) return null;
      const id = String(form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
      return id ? Number(id) : null;
    })();

    this.addIssueModalOpen = false;
    this.isLoading = true;

    // Issue documents
    const documents = await Promise.all(
      files.map(async (file: File) => {
        const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
        const fileContent = await this.readFileAsBase64(file);
        return {
          FileName: file.name,
          StorageFilePath: `${this.reviewId}/${form.issueId}/${file.name}`,
          fileContent,
          DocumentType: '',
          FileSize: file.size,
          FileType: ext,
          Description: '',
          UploadedBy: this.rebarAuth.getUserName() ?? '',
        };
      }),
    );

    // IssueFieldsJSON EAV array
    const issueFields = [
      { FieldName: 'IssueTitle',              FieldValue: form.issueTitle ?? '',                                                                                                   DataType: 'NVARCHAR' },
      { FieldName: 'IssueDescription',        FieldValue: form.issueDescription ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'ReviewObjective',         FieldValue: form.reviewObjective ?? '',                                                                                             DataType: 'NVARCHAR' },
      { FieldName: 'Recommendation',          FieldValue: form.recommendation ?? '',                                                                                              DataType: 'NVARCHAR' },
      { FieldName: 'ActionRequiredBy',        FieldValue: form.actionRequiredBy ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'BroadIssueCategoryL1',    FieldValue: form.broadCategory ?? '',                                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'DetailedIssueCategoryL2', FieldValue: form.detailedCategory ?? '',                                                                                            DataType: 'NVARCHAR' },
      { FieldName: 'RiskStatementL3',         FieldValue: form.riskStatement ?? '',                                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'IdentificationDate',      FieldValue: this.toDisplayDate(form.identificationDate),                                                                            DataType: 'DATE'     },
      ...(form.issueCloseDueDate               ? [{ FieldName: 'IssueCloseDueDate',       FieldValue: form.issueCloseDueDate,                                                    DataType: 'DATE' }] : []),
      { FieldName: 'IssueRating',             FieldValue: form.issueRating ?? '',                                                                                                 DataType: 'NVARCHAR' },
      { FieldName: 'ReportStatus',            FieldValue: form.reportStatus ?? '',                                                                                                DataType: 'NVARCHAR' },
      { FieldName: 'RepLetterConflict',       FieldValue: form.repLetterConflict === 'Yes' ? 'Yes' : form.repLetterConflict === 'No' ? 'No' : 'Not Applicable',                    DataType: 'NVARCHAR' },
      { FieldName: 'RepLetterQuestions',      FieldValue: form.repLetterConflict === 'Yes' && form.repLetterQuestionsWithConflict?.length ? form.repLetterQuestionsWithConflict.join(',') : 'N/A', DataType: 'NVARCHAR' },
      { FieldName: 'ServiceLine',             FieldValue: (form.serviceLines ?? []).join(','),                                                                                    DataType: 'NVARCHAR' },
      { FieldName: 'RCReviewer',              FieldValue: form.rcReviewer ?? '',                                                                                                  DataType: 'NVARCHAR' },
      { FieldName: 'RCSupervisor',            FieldValue: form.rcSupervisor ?? '',                                                                                                DataType: 'NVARCHAR' },
      { FieldName: 'RCManager',               FieldValue: form.rcManager ?? '',                                                                                                   DataType: 'NVARCHAR' },
      { FieldName: 'RCComments',              FieldValue: form.rcCommentsForIssue ?? '',                                                                                          DataType: 'NVARCHAR' },
      { FieldName: 'LocationReviewedImpacted',FieldValue: (form.locationsImpacted ?? []).join(','),                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'LocationNames',           FieldValue: (form.locationsImpacted ?? []).join(','),                                                                               DataType: 'NVARCHAR' },
      { FieldName: 'SubscriberNames',         FieldValue: (form.subscribers ?? []).join(','),                                                                                     DataType: 'NVARCHAR' },
      { FieldName: 'LegacyIssueNumber',        FieldValue: form.legacyIssueNumber ?? '',                                                                                          DataType: 'NVARCHAR' },
      { FieldName: 'IssueFromGoLive',         FieldValue: form.issueFromGoLive ?? '',                                                                                             DataType: 'NVARCHAR' },
      { FieldName: 'DocumentsJSON',           FieldValue: documents.length ? JSON.stringify(documents) : '[]',                                                                    DataType: 'NVARCHAR' },
    ];

    // ActionPlansJSON — existing linked APs + new APs
    const actionPlansJson: any[] = [];

    for (const ap of (payload.actionPlans ?? [])) {
      const numericId = String(ap.id ?? '').replace(/^AP-/i, '').trim();
      const orig: any = this.actionPlans.find((a: any) => {
        const rawId = String(a.ActionPlanID ?? a.ActionPlanId ?? a.ID ?? a.Id ?? a.ActionID ?? '').trim().replace(/^AP-/i, '');
        return rawId === numericId;
      }) ?? {};

      actionPlansJson.push({
        ActionPlanID:   numericId ? Number(numericId) : null,
        ActionPlanName: ap.id ?? '',
        ReviewID:       this.reviewId,
        Fields: [
          { FieldName: 'AP_Description',        FieldValue: orig.AP_Description ?? orig.ActionPlanDescription ?? orig.Description ?? '',                          DataType: 'NVARCHAR' },
          { FieldName: 'AP_OwnerEmail',          FieldValue: orig.AP_OwnerEmail ?? orig.OwnerEmail ?? '',                                                             DataType: 'NVARCHAR' },
          ...(orig.AP_TargetDate ?? orig.TargetDateForActionPlanRemediationAction ?? orig.TargetDate ? [{ FieldName: 'AP_TargetDate', FieldValue: String(orig.AP_TargetDate ?? orig.TargetDateForActionPlanRemediationAction ?? orig.TargetDate).split('T')[0], DataType: 'DATE' }] : []),
          { FieldName: 'AP_Status',              FieldValue: orig.AP_Status ?? orig.Status ?? orig.ActionPlanStatus ?? '',                                            DataType: 'NVARCHAR' },
          { FieldName: 'AP_InternalDependency',  FieldValue: orig.AP_InternalDependency ?? orig.InternalDependency ?? '',                                             DataType: 'NVARCHAR' },
          { FieldName: 'AP_ClientDependency',    FieldValue: orig.AP_ClientDependency ?? orig.ClientDependency ?? '',                                                 DataType: 'NVARCHAR' },
          ...(orig.AP_ClosedDate ?? orig.ClosedDate ? [{ FieldName: 'AP_ClosedDate', FieldValue: String(orig.AP_ClosedDate ?? orig.ClosedDate).split('T')[0], DataType: 'DATE' }] : []),
          ...(orig.AP_RevisedTargetDate ?? orig.RevisedTargetDate ? [{ FieldName: 'AP_RevisedTargetDate', FieldValue: String(orig.AP_RevisedTargetDate ?? orig.RevisedTargetDate).split('T')[0], DataType: 'DATE' }] : []),
          { FieldName: 'DocumentsJSON',          FieldValue: '[]',                                                                                                   DataType: 'NVARCHAR' },
        ],
      });
    }

    const newAps: any[] = payload.newActionPlans ?? [];
    const newApFilesArray: any[][] = payload.newActionPlanFiles ?? [];
    for (let i = 0; i < newAps.length; i++) {
      const newAp = newAps[i];
      const newApFiles: any[] = newApFilesArray[i] ?? [];
      const newApOwnerEmail = (newAp.owners ?? []).map((o: string) => o.includes('@') ? o : `${o}@accenture.com`).join(';');
      const newApDocuments = await Promise.all(
        newApFiles.map(async (file: File) => {
          const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : '';
          const fileContent = await this.readFileAsBase64(file);
          return {
            FileName: file.name,
            StorageFilePath: `${this.reviewId}/${form.issueId}/${newAp.id}/${file.name}`,
            fileContent,
            DocumentType: '',
            FileSize: file.size,
            FileType: ext,
            Description: '',
            UploadedBy: this.rebarAuth.getUserName() ?? '',
          };
        }),
      );

      actionPlansJson.push({
        ActionPlanID:   null,
        ActionPlanName: null,
        ReviewID:       this.reviewId,
        Fields: [
          { FieldName: 'AP_Description',       FieldValue: newAp.description ?? '',                                                                                                                    DataType: 'NVARCHAR' },
          { FieldName: 'AP_OwnerEmail',         FieldValue: newApOwnerEmail,                                                                                                                            DataType: 'NVARCHAR' },
          ...(newAp.targetDate        ? [{ FieldName: 'AP_TargetDate',        FieldValue: newAp.targetDate,        DataType: 'DATE' }] : []),
          { FieldName: 'AP_Status',             FieldValue: newAp.status?.trim() || '',                           DataType: 'NVARCHAR' },
          { FieldName: 'AP_InternalDependency', FieldValue: (newAp.internalDependencies ?? []).join(','),                                                                                               DataType: 'NVARCHAR' },
          { FieldName: 'AP_ClientDependency',   FieldValue: newAp.clientDependency ?? '',                                                                                                               DataType: 'NVARCHAR' },
          ...(newAp.closedDate        ? [{ FieldName: 'AP_ClosedDate',        FieldValue: newAp.closedDate,        DataType: 'DATE' }] : []),
          ...(newAp.revisedTargetDate ? [{ FieldName: 'AP_RevisedTargetDate', FieldValue: newAp.revisedTargetDate, DataType: 'DATE' }] : []),
          { FieldName: 'LegacyAPNumber',        FieldValue: newAp.legacyApNumber ?? '',                                                                                                                 DataType: 'NVARCHAR' },
          { FieldName: 'AP_RCComments',         FieldValue: newAp.rcComments ?? '',                                                                                                                    DataType: 'NVARCHAR' },
          ...(newAp.redClientInformedDate        ? [{ FieldName: 'AP_RedClientDate',     FieldValue: newAp.redClientInformedDate,       DataType: 'DATE' }] : []),
          ...(newAp.targetDateToInformRedClient  ? [{ FieldName: 'TargetDateclientinfo', FieldValue: newAp.targetDateToInformRedClient, DataType: 'DATE' }] : []),
          { FieldName: 'DocumentsJSON',         FieldValue: newApDocuments.length ? JSON.stringify(newApDocuments) : '[]',                                                                              DataType: 'NVARCHAR' },
        ],
      });
    }

    const eavParams = {
      category:        'UpsertIssueWithActionPlan_EAV',
      IssueID:         issueIdForUpdate,
      ReviewID:        this.reviewId,
      ActionPlanID:    null,
      IssueName:       form.issueTitle,
      UserID:          this.rebarAuth.getUserName() ?? '',
      ActionType:      actionType,
      IssueFieldsJSON: JSON.stringify(issueFields),
      ActionPlansJSON: JSON.stringify(actionPlansJson),
    };

    this.reviewerService.UpsertIssueWithActionPlanEAV(eavParams)
      .pipe(timeout(30000))
      .subscribe({
        next: (response: any) => {
          const formCacheId = String(form.issueId || '').replace(/^[A-Za-z]+-/i, '').trim();
          const responseObj = Array.isArray(response) ? response[0] : response;
          const responseCacheId = String(
            responseObj?.NewIssueID ?? responseObj?.newIssueId ?? responseObj?.IssueID ?? responseObj?.issueId ?? responseObj?.IssueId ?? responseObj?.data?.IssueID ?? ''
          ).replace(/^[A-Za-z]+-/i, '').trim();
          const subs: string[] = form.subscribers ?? [];
          const existingApIds = (payload.actionPlans ?? []).map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim()).filter(Boolean);
          const newApIds = (payload.newActionPlans ?? []).map((ap: any) => String(ap.id ?? '').replace(/^AP-/i, '').trim()).filter(Boolean);
          const apIds = [...new Set([...existingApIds, ...newApIds])];
          for (const id of Array.from(new Set([formCacheId, responseCacheId].filter((k: string) => k)))) {
            this.issueSubscriberCache.set(id, [...subs]);
            try { localStorage.setItem(`ast_subs_${id}`, JSON.stringify(subs)); } catch {}
            try { localStorage.setItem(`ast_linked_aps_${id}`, JSON.stringify(apIds)); } catch {}
            this._linkedApFixes.set(id, apIds);
            // Cache file metadata so re-opening edit modal shows previously attached files.
            try {
              const prev: any[] = (() => { try { return JSON.parse(localStorage.getItem(`ast_docs_${id}`) ?? '[]'); } catch { return []; } })();
              const deleted = new Set<string>((payload as any).deletedFilePaths ?? []);
              const kept = prev.filter((f: any) => !deleted.has(f.StorageFilePath));
              const added = (payload.files as File[]).map((f: File) => ({
                FileName: f.name,
                StorageFilePath: `${this.reviewId}/${form.issueId}/${f.name}`,
                FileSize: f.size,
                FileType: f.name.split('.').pop() ?? '',
              }));
              localStorage.setItem(`ast_docs_${id}`, JSON.stringify([...kept, ...added]));
            } catch {}
          }
          const newIssueId = Number(responseCacheId) || Number(formCacheId);
          const doFinish = () => {
            this.draftSavedMessage = true;
            setTimeout(() => (this.draftSavedMessage = false), 3000);
            this.showIssueGrid = true;
            this.loadIssues();
            this.reviewerService.notifyActionPlanSaved();
          };
          doFinish();
        },
        error: (err) => {
          console.error('UpsertIssueWithActionPlanEAV error:', err?.status, err?.message);
          this.isLoading = false;
        },
      });
  }

  private toFinancialYearId(fy: string): string | null {
    if (!fy) return null;
    const digits = fy.replace(/\D/g, '');
    return digits ? '20' + digits : null;
  }


  private toDisplayDate(iso: string): string {
    return this.dateFormatSvc.format(iso);
  }

  /** Read a File as base64 (strips the data-URI prefix). */
  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const commaIndex = result.indexOf(',');
        resolve(commaIndex >= 0 ? result.substring(commaIndex + 1) : result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private loadLocations() {
    if (this.reviewId == null) return;
    this.reviewerService.getLocationNamesByReviewId(this.reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (Array.isArray(response)) {
            const locs: string[] = response
              .map((item: any) => item.LocationName ?? item.LocationCity ?? item.Name ?? item)
              .filter((v: any) => typeof v === 'string' && v.trim().length > 0);
            this.locations = locs;
          }
        },
        error: () => {},
      });
  }

  /**
   * Fetch the Issue ID to preview in the popup (GetLastInsertedIssueByReviewID).
   * Shows the NEXT id = last inserted + 1 (so null/no issues → I-1, 17 → I-18).
   */
  private loadNextIssueId() {
    this.nextIssueId = '';
    if (this.reviewId == null) return;
    this.reviewerService.getLastInsertedIssueByReviewId(this.reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const row = Array.isArray(response) ? response[0] : response;
          let idVal: any = null;
          if (row && typeof row === 'object') {
            idVal = row.IssueID ?? row.IssueId ?? row.LastIssueID
              ?? row.LastInsertedIssueID ?? row.NextIssueID ?? Object.values(row)[0];
          } else {
            idVal = row;
          }
          // Parse the numeric part of the last inserted id (null/blank → 0), then +1.
          const digits = idVal === null || idVal === undefined ? '' : String(idVal).replace(/\D/g, '');
          const lastNum = digits ? parseInt(digits, 10) : 0;
          this.nextIssueId = 'I-' + (lastNum + 1);
        },
        error: () => { this.nextIssueId = ''; },
      });
  }

  /**
   * Fetch the Action Plan ID to display in the popup (GetLastInsertedActionPlanByReviewID).
   * Returns the LAST inserted ID; we display last + 1 so the new AP gets a unique ID.
   */
  private loadNextActionPlanId() {
    this.nextActionPlanId = '';
    if (this.reviewId == null) return;
    this.reviewerService.getLastInsertedActionPlanByReviewId(this.reviewId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const row = Array.isArray(response) ? response[0] : response;
          let idVal: any = null;
          if (row && typeof row === 'object') {
            idVal = row.ActionPlanName ?? row.ActionPlanId ?? row.ActionPlanID ?? Object.values(row)[0];
          } else {
            idVal = row;
          }
          const digits = idVal === null || idVal === undefined ? '' : String(idVal).replace(/\D/g, '');
          const lastNum = digits ? parseInt(digits, 10) : 0;
          this.nextActionPlanId = 'AP-' + (lastNum + 1);
        },
        error: () => { this.nextActionPlanId = ''; },
      });
  }

  /** Fetch the existing action plans for the current review (Step 2 list). */
  private loadActionPlans() {
    this.actionPlans = [];
    if (this.reviewId == null) return;
    const apObs = this.reviewerService.getActionPlansEAV(this.reviewId);
    apObs
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const list = Array.isArray(response)
            ? response
            : (response?.actionPlans ?? response?.ActionPlans ?? response?.ActionPlan ?? []);
          this.actionPlans = Array.isArray(list) ? list : [];
        },
        error: () => { this.actionPlans = []; },
      });
  }

  private loadRepLetterQuestions() {
    this.reviewerService.getRepletterQuestions().subscribe({
      next: (response: any) => {
        if (Array.isArray(response)) {
          const questions: string[] = response
            .map((item: any) => item.RepletterQuestions ?? item.RepLetterQuestions ?? item.Name ?? item)
            .filter((v: any) => typeof v === 'string' && v.trim().length > 0);
          if (questions.length) this.repLetterQuestions = questions;
        }
      },
      error: () => {},
    });
  }

  private loadInternalDependencies() {
    this.reviewerService.getInternalDependencies().subscribe({
      next: (response: any) => {
        const list = Array.isArray(response) ? response : [];
        const deps: string[] = list
          .map((item: any) => {
            if (typeof item === 'string') return item;
            // Try all likely column name variants from the stored procedure
            const val =
              item.InternalDependencyName ??
              item.InternalDependency ??
              item.DependencyName ??
              item.Dependency ??
              item.Name ??
              item.Value ??
              item.Text;
            if (val !== undefined && val !== null) return String(val);
            // Last resort: first non-empty string property on the object
            const firstStr = Object.values(item).find(
              (v) => typeof v === 'string' && (v as string).trim().length > 0
            );
            return firstStr as string ?? null;
          })
          .filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
        if (deps.length) this.internalDependencies = deps;
      },
      error: () => {},
    });
  }

  private loadRiskCategories() {
    this.reviewerService.getRiskCategories().subscribe({
      next: (response: any) => {
        // API shape: [{ RiskCategories: "<JSON string of nested L1/L2/L3 tree>" }]
        const raw = Array.isArray(response) ? response[0]?.RiskCategories : response?.RiskCategories;
        try {
          const parsed = raw ? JSON.parse(raw) : [];
          this.riskCategories = Array.isArray(parsed) ? parsed : [];
        } catch {
          this.riskCategories = [];
        }
      },
      error: () => {},
    });
  }
}